import React from "react";
import { Chip, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import LaunchIcon from "@material-ui/icons/Launch";

const useStyles = makeStyles((theme) => ({
  card: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minWidth: 0,
    overflow: "hidden",
    borderRadius: 20,
    background: "linear-gradient(145deg,var(--surface-strong),var(--surface))",
    border: "1px solid var(--border)",
    boxShadow: "var(--shadow-sm)",
    transition: "transform .23s ease, border-color .23s ease, box-shadow .23s ease",
    "&:hover": {
      transform: "translateY(-5px)",
      borderColor: "var(--border-accent)",
      boxShadow: "var(--shadow-md)",
    },
  },
  media: {
    position: "relative",
    aspectRatio: "16 / 9",
    overflow: "hidden",
    background: "var(--page-bg-soft)",
  },
  image: {
    width: "100%",
    height: "100%",
    objectFit: "cover",
    transition: "transform .35s ease",
    "$card:hover &": { transform: "scale(1.03)" },
  },
  body: {
    display: "flex",
    flexDirection: "column",
    flex: 1,
    minWidth: 0,
    padding: 20,
  },
  meta: {
    display: "flex",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 7,
    marginBottom: 11,
    color: "var(--accent)",
    fontSize: 9,
    fontWeight: 850,
    letterSpacing: theme.direction === "rtl" ? 0 : ".07em",
    textTransform: theme.direction === "rtl" ? "none" : "uppercase",
  },
  metaDot: { color: "var(--text-muted)" },
  title: {
    color: "var(--text-primary)",
    fontWeight: 900,
    fontSize: "clamp(1.15rem,2vw,1.45rem)",
    lineHeight: 1.5,
    marginBottom: 10,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  desc: {
    color: "var(--text-secondary)",
    fontSize: 12.5,
    lineHeight: 1.88,
    marginBottom: 16,
    textAlign: theme.direction === "rtl" ? "right" : "left",
  },
  tags: {
    display: "flex",
    flexWrap: "wrap",
    gap: 6,
    marginTop: "auto",
  },
  tag: {
    height: 26,
    color: "var(--text-soft)",
    fontSize: 9,
    background: "var(--accent-soft)",
    border: "1px solid var(--border-accent)",
  },
  link: {
    display: "inline-flex",
    alignItems: "center",
    gap: 7,
    width: "fit-content",
    marginTop: 18,
    color: "var(--accent)",
    fontSize: 10.5,
    fontWeight: 850,
    transition: "color .2s ease, transform .2s ease",
    "&:hover": {
      color: "var(--accent-2)",
      transform: theme.direction === "rtl" ? "translateX(-2px)" : "translateX(2px)",
    },
  },
  icon: { transform: theme.direction === "rtl" ? "scaleX(-1)" : "none" },
}));

export default function Blog({ item, readLabel }) {
  const classes = useStyles();

  return (
    <article className={classes.card}>
      <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.media} aria-label={item.title}>
        <img src={item.image} alt={item.title} className={classes.image} loading="lazy" />
      </a>

      <div className={classes.body}>
        <div className={classes.meta}>
          <span>{item.source}</span>
          <span className={classes.metaDot}>•</span>
          <span>{item.readTime}</span>
        </div>

        <Typography component="h2" className={classes.title}>{item.title}</Typography>
        <div className={classes.desc}>{item.desc}</div>

        <div className={classes.tags}>
          {item.tags.map((tag) => (
            <Chip key={tag} label={tag} variant="outlined" className={classes.tag} />
          ))}
        </div>

        <a href={item.href} target="_blank" rel="noopener noreferrer" className={classes.link}>
          <span>{readLabel}</span><LaunchIcon className={classes.icon} fontSize="small" />
        </a>
      </div>
    </article>
  );
}
